import React, { useCallback, useEffect } from 'react';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import styles from './components/styles.module.css';
import SidePanelController from './controllers/SidePanelController';
import SidePanelHeader from './components/SidePanelHeader';
import SidePanelSlider from './components/SidePanelSlider';
import SidePanelPullTabs from './components/SidePanelPullTabs';
import { type AhaSidepanelToggleOpenEvent } from '@aha-app/aha-base';
import classNames from 'classnames';
import customElementBoolAttr from '../../utils/customElementBoolAttr';

interface SidePanelProps {
  className?: string;
  style?: React.CSSProperties;
}

const SidePanel: React.FC<SidePanelProps> = ({ className, style }) => {
  const controller = useController(SidePanelController);
  const { sidePanelOpen, hidden } = controller.state;
  const ref = React.useRef<HTMLDivElement>(null);

  const onOpen = useCallback(
    (event: CustomEvent<AhaSidepanelToggleOpenEvent>) => {
      controller.actionToggleOpenState(event.detail.open);

      if (event.detail.open) {
        window.AhaAnalytics?.track(
          'Feature-7249764725472497103',
          `User opens side panel`,
          {
            'Document type': controller.props.documentType,
            'Used template': Boolean(
              controller.props.sidePanelItems.find(
                i => i.id === 'best-practices'
              )
            ),
          }
        );
      }
    },
    [controller]
  );

  useEffect(() => {
    const panel = ref.current;
    if (panel) {
      panel.addEventListener('aha-sidepanel:toggleopen', onOpen);
    }

    return () => {
      if (panel) {
        panel.removeEventListener('aha-sidepanel:toggleopen', onOpen);
      }
    };
  }, [onOpen]);

  return (
    <div
      className={classNames(styles.sidePanel, className)}
      style={hidden ? { display: 'none' } : {}}
    >
      <aha-sidepanel
        ref={ref}
        open={customElementBoolAttr(sidePanelOpen)}
        style={style}
      >
        <div slot='pull-tabs'>
          <SidePanelPullTabs />
        </div>

        <div>
          <SidePanelHeader />
          <SidePanelSlider />
        </div>
      </aha-sidepanel>
    </div>
  );
};

const ControlledSidePanel = StartControllerScope(
  SidePanelController,
  ApplicationView(SidePanel)
);

export default ControlledSidePanel;
